import { datadogLogs, LogsInitConfiguration } from "@datadog/browser-logs";
import { datadogRum, DefaultPrivacyLevel, RumInitConfiguration } from "@datadog/browser-rum";
import posthog from "posthog-js";
import { getGraphQLBaseUrl, getStage } from "src/context";

/* Since Stage is meaningful in the GraphQL context as well, grab off the ReturnType vs. exporting from context.ts */
type Stage = ReturnType<typeof getStage>;

/** Enable third-party analytics tools */
export function enableAnalytics(stage = getStage()) {
  enablePosthog(stage);
  const datadogEnabled = enableDatadog(stage);

  if (datadogEnabled) {
    datadogRum.startSessionReplayRecording();
  }
}

export function setAnalyticsUser(user: { id?: string; email?: string; name?: string }): void {
  const { id, email, name } = user;
  // Attach user identity to PostHog
  posthog.identify(`internal_users_${id}`, { email, name });

  // Identify user with Datadog Real User Monitoring
  // https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#identify-user-sessions
  if (id) {
    datadogRum.setUser({ id, name, email });
    datadogLogs.setUser({ id, name, email });
  } else {
    datadogRum.clearUser();
    // There isn't a datadogLogs.removeUser, so remove each key
    ["id", "name", "email"].forEach((key) => datadogLogs.removeUserProperty(key));
  }
}

const DATADOG_IGNORED_ERROR_MESSAGES = /ResizeObserver loop/;
function enableDatadog(stage: Stage): boolean {
  if (stage === "local") {
    return false;
  }

  const commonOps: Omit<RumInitConfiguration, "applicationId"> & LogsInitConfiguration = {
    clientToken: "pubdc77de404dd21254bc21540990ce1e3c",
    site: "datadoghq.com",
    service: "internal-frontend",
    env: stage,
    version: VITE_GIT_COMMIT ?? "local",
    // Our tests call init multiple times to test our setup logic
    silentMultipleInit: true,
  };

  // https://docs.datadoghq.com/real_user_monitoring/browser/#configuration
  datadogRum.init({
    ...commonOps,
    applicationId: "75b6767e-587c-4c6f-9354-8157c2d9173d",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    allowedTracingUrls: [getGraphQLBaseUrl()],
    // https://docs.datadoghq.com/real_user_monitoring/application_monitoring/browser/data_collected/#graphql-attributes
    allowedGraphQlUrls: [/\/graphql$/],
    beforeSend: (event) => {
      // https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#enrich-and-control-rum-data
      if (event.type === "error" && DATADOG_IGNORED_ERROR_MESSAGES.test(event.error.message)) {
        return false;
      }

      return true;
    },
    defaultPrivacyLevel: DefaultPrivacyLevel.ALLOW,
    enableExperimentalFeatures: [
      "clickmap", // enable heatmap (https://docs.datadoghq.com/real_user_monitoring/heatmaps/#prerequisites)
    ],
  });

  datadogLogs.init({
    ...commonOps,
    forwardConsoleLogs: [
      // Remember that we pay per-log ingested. You should likely avoid reporting debug-level log messages
      // unless you're actively debugging a major, global issue.
      // "debug",
      "info",
      "warn",
      "error",
    ],
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    beforeSend: (event) => {
      // Abort errors are not necessarily error in the app. They are typically triggered by the user navigating away from
      // a page and causing an in-flight request to be canceled. We don't want to report these as errors.
      if (event.error && event.error.stack?.startsWith("AbortError:")) {
        return false;
      }
      return !DATADOG_IGNORED_ERROR_MESSAGES.test(event.message);
    },
  });

  return true;
}

function enablePosthog(stage: Stage) {
  if (stage !== "prod") {
    return false;
  }

  posthog.init("phc_813WZxVPPPSHtUKAmrG7PNdXHXWvkPyexWzcsJV5KEk", {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
  });
}
